import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api, { CATEGORY_IMAGES, CATEGORY_META, RARITY_META } from '../lib/api';
import { ArrowLeft, Sparkles, Search, Check, Lock, ArrowDownUp, Star } from 'lucide-react';

// Milliseconds threshold for the "recently discovered" pulse ring.
const RECENT_MS = 24 * 60 * 60 * 1000;

// -----------------------------------------------------------------------
// CategoryTile — one card on the collections index. Loads the topic photo
// with a graceful gradient/monogram fallback if the CDN 404s so a bad
// remote image never blocks the page from looking finished.
// -----------------------------------------------------------------------
function CategoryTile({ c, onSelect, delay }) {
  const meta = CATEGORY_META[c.category] || { tint: 'from-emerald-700/70 to-teal-900/60', glyph: c.category[0] };
  const [broken, setBroken] = useState(false);
  const pct = Math.max(0, Math.min(100, c.percent || 0));

  // "Recently discovered" pulse ring: fires if the caller unlocked a new
  // species in this category within the last 24 hours.
  const isFresh = useMemo(() => {
    if (!c.latest_discovered_at) return false;
    const t = Date.parse(c.latest_discovered_at);
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) < RECENT_MS;
  }, [c.latest_discovered_at]);

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`category-${c.category}`}
      onClick={() => onSelect(c.category)}
      className={`relative rounded-3xl overflow-hidden aspect-[4/5] text-left group border transition-colors bg-black
        ${isFresh ? 'border-accent/70 shadow-[0_0_0_1px_rgba(233,196,106,0.35),0_0_28px_-4px_rgba(233,196,106,0.55)]'
                  : 'border-white/5 hover:border-accent/40'}`}
    >
      {/* Pulsing accent ring for recent discoveries */}
      {isFresh && (
        <>
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -inset-0.5 rounded-3xl border-2 border-accent/60"
            animate={{ opacity: [0.9, 0.35, 0.9], scale: [1, 1.015, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <span className="absolute top-3 left-3 z-10 px-2 py-0.5 rounded-full bg-accent text-black text-[9px] uppercase tracking-widest font-bold flex items-center gap-1"
                data-testid={`category-fresh-${c.category}`}>
            <Sparkles className="w-2.5 h-2.5" strokeWidth={3} /> New
          </span>
        </>
      )}

      {/* Photo layer with graceful fallback */}
      {CATEGORY_IMAGES[c.category] && !broken ? (
        <img
          src={CATEGORY_IMAGES[c.category]}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${meta.tint} flex items-center justify-center`}>
          <span className="font-serif text-[7rem] leading-none text-white/25 select-none">{meta.glyph}</span>
        </div>
      )}

      {/* Tinted overlay that always brings the type + label forward */}
      <div className={`absolute inset-0 bg-gradient-to-br ${meta.tint} mix-blend-multiply opacity-60`} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#040A08] via-[#040A08]/40 to-transparent" />

      {/* Percent chip */}
      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[11px] font-mono tabular-nums flex items-center gap-1">
        {pct === 100 && <Check className="w-3 h-3 text-emerald-300" strokeWidth={3} />}
        {pct}%
      </div>

      {/* Bottom sheet with name / count / progress */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="font-serif text-xl leading-tight drop-shadow-lg">{c.category}</p>
        <div className="flex items-baseline gap-2 mt-1">
          <p className="text-xs font-mono tabular-nums text-white/80">{c.discovered}/{c.total}</p>
          <p className="text-[10px] uppercase tracking-widest text-white/50">species</p>
        </div>
        <div className="mt-2.5 h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ delay: delay + 0.15, duration: 0.7, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-accent to-emerald-300"
          />
        </div>
      </div>
    </motion.button>
  );
}

// -----------------------------------------------------------------------
// SpeciesTile — a card inside a category detail view.
// -----------------------------------------------------------------------
function SpeciesTile({ s, categoryFallback }) {
  const nav = useNavigate();
  const meta = CATEGORY_META[categoryFallback] || { tint: 'from-emerald-700/70 to-teal-900/60', glyph: '?' };
  const [broken, setBroken] = useState(false);
  const src = s.image_url || CATEGORY_IMAGES[categoryFallback];
  const rarity = RARITY_META[s.rarity] || RARITY_META.common;

  return (
    <button
      type="button"
      onClick={() => s.discovered && nav(`/species/${s.species_id}`)}
      disabled={!s.discovered}
      data-testid={s.discovered ? `species-card-${s.species_id}` : `species-locked-${s.species_id}`}
      className={`text-left rounded-2xl bg-card border overflow-hidden disabled:cursor-not-allowed transition
        ${s.discovered ? 'border-white/10 hover:border-accent/60 hover:-translate-y-0.5' : 'border-white/5'}`}
    >
      <div className="aspect-square relative">
        {!broken && src ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            className={`w-full h-full object-cover ${s.discovered ? '' : 'grayscale opacity-30'}`}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${meta.tint} flex items-center justify-center ${s.discovered ? '' : 'opacity-30'}`}>
            <span className="font-serif text-5xl text-white/30 select-none">{meta.glyph}</span>
          </div>
        )}
        {!s.discovered && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Lock className="w-5 h-5 text-white/50" strokeWidth={2} />
          </div>
        )}
        {/* First-Captor gold star — permanent public credit to the very
            first person to discover this species anywhere in the game. */}
        {s.first_captor_nickname && (
          <div
            className={`absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-semibold border ${
              s.first_captor_is_you
                ? 'bg-amber-500/25 border-amber-300/70 text-amber-100'
                : 'bg-black/55 border-amber-300/40 text-amber-200'
            }`}
            title={s.first_captor_is_you
              ? 'You were the first explorer to discover this species'
              : `First discovered by @${s.first_captor_nickname}`}
            data-testid={`first-captor-${s.species_id}`}
          >
            <Star className="w-2.5 h-2.5" fill="currentColor" strokeWidth={0} />
            {s.first_captor_is_you ? 'You' : `@${s.first_captor_nickname}`}
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#040A08] via-[#040A08]/70 to-transparent p-2.5">
          <p className="text-xs font-medium truncate">{s.discovered ? s.common_name : '???'}</p>
          <p className="text-[9px] uppercase tracking-widest mt-0.5" style={{ color: rarity.color }}>{rarity.label}</p>
        </div>
      </div>
    </button>
  );
}

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------
export default function Collections() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');    // 'all' | 'found' | 'locked'
  const [sort, setSort] = useState('default');    // 'default' | 'completion' | 'alpha' | 'closest'

  useEffect(() => { api.get('/collections').then(r => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selected) { setDetails(null); return; }
    api.get(`/collections/${selected}`).then(r => setDetails(r.data)).catch(() => {});
  }, [selected]);

  // Aggregate stats for the summary strip
  const summary = useMemo(() => {
    const totalDiscovered = cats.reduce((n, c) => n + (c.discovered || 0), 0);
    const totalSpecies    = cats.reduce((n, c) => n + (c.total || 0), 0);
    const percent = totalSpecies ? Math.round((totalDiscovered / totalSpecies) * 100) : 0;
    const completed = cats.filter(c => c.total > 0 && c.discovered === c.total).length;
    return { totalDiscovered, totalSpecies, percent, completed };
  }, [cats]);

  // Sorted category list — mirrors the "chase easy wins" pattern from
  // rank-collection games. "closest" surfaces the collection with the
  // fewest remaining unlocks first (excluding completed ones).
  const sortedCats = useMemo(() => {
    const arr = [...cats];
    if (sort === 'completion')  arr.sort((a, b) => (b.percent || 0) - (a.percent || 0));
    else if (sort === 'alpha')  arr.sort((a, b) => a.category.localeCompare(b.category));
    else if (sort === 'closest') arr.sort((a, b) => {
      const remA = (a.total || 0) - (a.discovered || 0);
      const remB = (b.total || 0) - (b.discovered || 0);
      // Completed collections drop to the bottom; among the rest, the
      // smallest-remaining category wins.
      const scoreA = remA === 0 ? Infinity : remA;
      const scoreB = remB === 0 ? Infinity : remB;
      return scoreA - scoreB;
    });
    return arr;
  }, [cats, sort]);

  // ---- Detail view -----------------------------------------------------
  if (selected) {
    const filtered = (details?.species || []).filter(s => {
      if (filter === 'found' && !s.discovered) return false;
      if (filter === 'locked' && s.discovered) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (s.common_name || '').toLowerCase().includes(q) ||
             (s.scientific_name || '').toLowerCase().includes(q);
    });
    const foundCount = (details?.species || []).filter(s => s.discovered).length;
    const totalCount = details?.species?.length || 0;

    return (
      <div className="space-y-5" data-testid="collection-detail">
        <button
          data-testid="back-collections"
          onClick={() => { setSelected(null); setQuery(''); setFilter('all'); }}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition"
        >
          <ArrowLeft className="w-4 h-4" /> All Collections
        </button>

        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="font-serif text-3xl">{selected}</h2>
            <p className="text-xs text-muted-foreground mt-1 font-mono tabular-nums">
              {foundCount}/{totalCount} discovered · {totalCount ? Math.round((foundCount/totalCount)*100) : 0}%
            </p>
          </div>
        </div>

        {/* Search + filter chips */}
        <div className="space-y-2.5">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search this collection…"
              data-testid="collection-search"
              className="w-full pl-9 pr-3 py-2.5 rounded-full bg-card border border-white/10 text-sm outline-none focus:border-accent/50 placeholder:text-muted-foreground/60"
            />
          </div>
          <div className="flex gap-2 text-[11px] uppercase tracking-widest">
            {['all', 'found', 'locked'].map(k => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`collection-filter-${k}`}
                className={`px-3 py-1.5 rounded-full border transition ${
                  filter === k
                    ? 'bg-accent text-black border-accent font-semibold'
                    : 'bg-white/5 border-white/10 text-muted-foreground hover:text-white'
                }`}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        {!details ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square bg-card rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-card/60 p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {query ? `No species match "${query}".` : filter === 'found'
                ? 'Nothing discovered here yet — go find your first one!'
                : 'All caught up in this collection.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filtered.map((s) => (
              <SpeciesTile key={s.species_id} s={s} categoryFallback={selected} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- Index view ------------------------------------------------------
  return (
    <div className="space-y-6" data-testid="collections-root">
      <div>
        <h2 className="font-serif text-3xl leading-tight">Collections</h2>
        <p className="text-sm text-muted-foreground mt-1">Every species you discover unlocks a card.</p>
      </div>

      {/* Summary strip */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-900/40 via-card/80 to-teal-900/30 p-4"
        data-testid="collections-summary"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-accent/15 border border-accent/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-accent" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Overall</p>
            <p className="font-serif text-2xl leading-tight">
              <span className="tabular-nums">{summary.totalDiscovered}</span>
              <span className="text-muted-foreground text-lg"> / {summary.totalSpecies}</span>
              <span className="text-accent text-sm ml-2 font-mono">{summary.percent}%</span>
            </p>
          </div>
          {summary.completed > 0 && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Complete</p>
              <p className="font-serif text-xl text-emerald-300 tabular-nums">{summary.completed}</p>
            </div>
          )}
        </div>
        <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${summary.percent}%` }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="h-full bg-gradient-to-r from-accent via-emerald-300 to-teal-300"
          />
        </div>
      </motion.div>

      {/* Rarity legend + sort chip row */}
      <div className="space-y-2 -mt-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:flex-nowrap sm:overflow-x-auto sm:no-scrollbar pb-0.5" data-testid="rarity-legend">
          {['common', 'uncommon', 'rare', 'epic', 'legendary'].map(k => (
            <span
              key={k}
              className="flex items-center gap-1.5 shrink-0"
              title={RARITY_META[k].label}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: RARITY_META[k].color, boxShadow: `0 0 6px ${RARITY_META[k].color}55` }}
              />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {RARITY_META[k].label}
              </span>
            </span>
          ))}
        </div>
        <div className="flex justify-end">
          <div className="relative">
            <ArrowDownUp className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              data-testid="collections-sort"
              className="pl-7 pr-2.5 py-1.5 rounded-full bg-card border border-white/10 text-[11px] uppercase tracking-widest outline-none focus:border-accent/50 cursor-pointer appearance-none"
            >
              <option value="default">Default</option>
              <option value="completion">By completion</option>
              <option value="alpha">A → Z</option>
              <option value="closest">Closest to finish</option>
            </select>
          </div>
        </div>
      </div>

      {/* Category grid */}
      {cats.length === 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-card rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {sortedCats.map((c, i) => (
            <CategoryTile key={c.category} c={c} onSelect={setSelected} delay={i * 0.04} />
          ))}
        </div>
      )}
    </div>
  );
}
